import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLaunchOfferStatus } from "./useLaunchOffer";
import { trackCustom } from "@/lib/meta-pixel";


const pad = (n: number) => n.toString().padStart(2, "0");

function useTimerParts(endMs: number) {
  const compute = () => {
    const total = Math.max(0, endMs - Date.now());
    return {
      days: Math.floor(total / 86_400_000),
      hours: Math.floor((total % 86_400_000) / 3_600_000),
      minutes: Math.floor((total % 3_600_000) / 60_000),
      seconds: Math.floor((total % 60_000) / 1000),
    };
  };
  const [p, setP] = useState(compute);
  useEffect(() => {
    setP(compute());
    const id = window.setInterval(() => setP(compute()), 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMs]);
  return p;
}

// Routes that must NEVER show the sticky promo banner.
const EXCLUDED_PATTERNS: RegExp[] = [
  /^\/admin(\/|$)/,
  /^\/privacy-policy/,
  /^\/cookie-policy/,
  /^\/terms-of-service/,
  /^\/disclaimer/,
  /^\/launch-offer-terms/,
  /^\/online(\/|$)/, // Solace Online page (also covers /online-classes-signup)
  /^\/$/, // homepage uses the hero banner instead
];

function isExcluded(pathname: string): boolean {
  return EXCLUDED_PATTERNS.some((re) => re.test(pathname));
}

/**
 * Slim sticky launch-offer banner shown across public, non-admin pages.
 */
export default function StickyPromoBanner() {
  const { active, phase, endMs, ctaUrl, promotionId, promotionName, discountPercentage, headline } =
    useLaunchOfferStatus();
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement | null>(null);
  const viewObsRef = useRef<IntersectionObserver | null>(null);

  const excluded = isExcluded(pathname);
  const show = active && !excluded;

  // Reserve layout space via CSS variable on <html>.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!show || !ref.current) {
      root.style.setProperty("--launch-offer-offset", "0px");
      return;
    }
    const apply = () => {
      const h = ref.current?.offsetHeight ?? 0;
      root.style.setProperty("--launch-offer-offset", `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(ref.current);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      root.style.setProperty("--launch-offer-offset", "0px");
    };
  }, [show]);

  useEffect(() => {
    if (!show || !ref.current) return;
    viewObsRef.current?.disconnect();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            trackCustom(
              "promotion_view",
              {
                promotion_id: promotionId,
                promotion_name: promotionName,
                promotion_phase: phase,
                placement: "sticky_banner",
              },
              { dedupeKey: `promo_view_sticky_banner_${promotionId}_${phase}` },
            );
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(ref.current);
    viewObsRef.current = obs;
    return () => obs.disconnect();
  }, [show, phase, promotionId, promotionName]);

  if (!show) return null;

  const onCtaClick = () => {
    trackCustom("promotion_click", {
      promotion_id: promotionId,
      promotion_phase: phase,
      placement: "sticky_banner",
      destination: ctaUrl,
    });
  };

  const label =
    phase === "last_chance"
      ? headline || `Last Chance: ${discountPercentage}% Off`
      : `${discountPercentage}% Off Launch Offer`;

  return (
    <Link
      to={ctaUrl}
      onClick={onCtaClick}
      ref={ref as any}
      role="region"
      aria-label={phase === "last_chance" ? "Last chance promotion" : "Launch offer"}
      className="fixed top-0 inset-x-0 z-[60] bg-[#FDFBF7] text-[#2C2C2C] border-b border-[#8B9D83]/30 shadow-sm block hover:bg-[#F5F2ED] transition-colors"
    >
      <BannerInner phase={phase} endMs={endMs} label={label} />
    </Link>
  );
}

function BannerInner({
  phase,
  endMs,
  label,
}: {
  phase: "launch" | "last_chance" | "off";
  endMs: number;
  label: string;
}) {
  const p = useTimerParts(endMs);
  const timer = `${p.days}:${pad(p.hours)}:${pad(p.minutes)}:${pad(p.seconds)}`;
  const showTimer = phase !== "last_chance" && endMs > 0;
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-2.5 flex items-center justify-center gap-3">
      <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#8B9D83] whitespace-nowrap">
        {label}
      </span>
      {showTimer && (
        <>
          <span className="text-[#2C2C2C]/30">·</span>
          <span
            className="text-xs font-semibold tracking-[0.18em] uppercase text-[#2C2C2C] tabular-nums whitespace-nowrap"
            role="timer"
          >
            {timer}
          </span>
        </>
      )}
    </div>
  );
}

